'use client';

import React from 'react';
import safeSpaceImg from '../../../public/images/safe-space-pin.png';
import { TypeMegaMenuComponentWithoutUnresolvableLinksResponse } from '@/features/contentful/_generated';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import Icon from '@/components/Icon/Icon';
import CustomImage from '@/components/CustomImage/CustomImage';

const socialIconImageMap: Record<string, () => JSX.Element> = {
    'Safe Spaces': () => (
        <CustomImage src={safeSpaceImg} alt="Safe space image" width={60} height={38} />
    ),
    LinkedIn: () => <Icon type="linkedin" width={32} height={32} className="" />,
};

const handleSocialClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    socialName: string,
    href: string,
): void => {
    event.preventDefault();

    switch (socialName) {
        case 'LinkedIn':
            window.dataLayer &&
                window.dataLayer.push({
                    event: 'linkedin_outbound_click',
                    linkedin_profile: href,
                });
            break;
        case 'Safe Spaces':
            window.dataLayer &&
                window.dataLayer.push({
                    event: 'safe_spaces_outbound_click',
                });
            break;
        case 'Twitter':
            window.dataLayer &&
                window.dataLayer.push({
                    event: 'twitter_outbound_click',
                });
            break;
        default:
            break;
    }

    window.open(href, '_blank');
};

export const FooterSocial: React.FC<{
    socialLinks: TypeMegaMenuComponentWithoutUnresolvableLinksResponse['fields']['socialLinks'];
    id: string;
}> = ({ socialLinks, id }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    if (!socialLinks) {
        return null;
    }

    const socialObj = socialLinks as Record<string, string>;

    return (
        <div className="social-wrap tw-flex tw-justify-center" id="safe-space">
            {Object.keys(socialObj).map((socialName, index) => {
                const inspector = inspectorProps({ fieldId: 'socialLinks' }) as any;
                const { key: inspectorKey, ...inspectorWithoutKey } = inspector;

                const iconElement = socialIconImageMap[socialName]?.();

                const renderedIcon = Array.isArray(iconElement)
                    ? React.Children.map(iconElement, (child, i) =>
                          React.isValidElement(child)
                              ? React.cloneElement(child, { key: i })
                              : child,
                      )
                    : iconElement;

                return (
                    <a
                        key={`${socialName}-${index}`}
                        href={socialObj[socialName]}
                        className="tw-py-[2.5px]"
                        target="_blank"
                        rel="noopener noreferrer"
                        {...inspectorWithoutKey}
                        onClick={(event) =>
                            handleSocialClick(event, socialName, socialObj[socialName])
                        }
                    >
                        <span className="hidden">{socialName}</span>
                        {renderedIcon}
                    </a>
                );
            })}
        </div>
    );
};

export default FooterSocial;
